import {useMemo, useRef, useState} from 'react'
import {
  GetPlaceDataForReservationSelectStoreQuery,
  GetStoresForReservationSelectStoreQuery
} from '@data/__generated__/types.main'
import useWebsiteId from '@hooks/useWebsiteId'
import {useQuery} from 'apollo-hooks'
import gql from 'graphql-tag'
import get from 'lodash/get'

import getDistanceFromLatLonInKm from './utils/coordinatesDistance'

function usePlaceData(placeId: string) {
  const result = useQuery<GetPlaceDataForReservationSelectStoreQuery>({
    query: gql`
      query getPlaceDataForReservationSelectStore($placeId: ID!) {
        place(placeId: $placeId) {
          _id
          text
          secondaryText
          location
        }
      }
    `,
    variables: {placeId},
    omit: !placeId,
    partial: true
  })

  return result.data.place
}

const GET_STORES = gql`
  query getStoresForReservationSelectStore($websiteId: ID) {
    stores(websiteId: $websiteId) {
      items {
        _id
        name
        phone
        supportOptions {
          phone
        }
        reservationsHumanSchedule {
          days
          schedule
        }
        zones {
          _id
          deliveryLimits
        }
        address {
          placeId
          location
          address
          addressSecondary
        }
      }
    }
  }
`

export type Store = GetStoresForReservationSelectStoreQuery['stores']['items'][0] & {
  distanceToAddress?: number
}

export default function useStoreLocator() {
  const websiteId = useWebsiteId()
  const map = useRef()
  const [selectedStore, setSelectedStore] = useState<Store>()
  const [placeId, setPlaceId] = useState<string>()

  const {data} = useQuery<GetStoresForReservationSelectStoreQuery>({
    query: GET_STORES,
    variables: {websiteId}
  })
  const place = usePlaceData(placeId)

  const stores = useMemo(() => {
    if (!place) return data.stores.items

    const {lat, lng} = place.location

    return data.stores.items
      .map(store => {
        const {lat: storeLat, lng: storeLng} = get(store, 'address.location', {})

        return {
          ...store,
          distanceToAddress:
            storeLat && storeLng ? getDistanceFromLatLonInKm(lat, lng, storeLat, storeLng) : 1000
        }
      })
      .sort((store1, store2) => store1.distanceToAddress - store2.distanceToAddress)
  }, [data.stores.items.length, place?._id])

  const setSelectedStoreWrapper = store => {
    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    map.current && map.current.panTo(store.address.location)
    setSelectedStore(store)
  }

  return {
    stores,
    map,
    selectedStore,
    setSelectedStore: setSelectedStoreWrapper,
    place,
    placeId,
    setPlaceId
  }
}
